import React from 'react';
import { ImageIcon, UploadIcon, PlusIcon, XIcon } from 'lucide-react';
import type { PM } from './useProductManager';
import { useDarkClasses } from '../../../hooks/useDarkClasses';
interface MediaUploadSectionProps {
  pm: PM;
}
export const MediaUploadSection: React.FC<MediaUploadSectionProps> = ({
  pm
}) => {
  const dk = useDarkClasses();
  const {
    darkMode,
    productType,
    editions,
    setEditions,
    selectedEditionId,
    setSelectedEditionId,
    setShowEditionModal,
    editionFileRefs,
    handleEditionFileChange,
    updateEditionMetadata,
    merchFiles,
    setMerchFiles,
    eventImageFile,
    setEventImageFile,
    eventBannerFile,
    setEventBannerFile,
    bookBoxFiles,
    setBookBoxFiles,
    creativePortfolioFiles,
    setCreativePortfolioFiles,
    servicePortfolioFiles,
    setServicePortfolioFiles
  } = pm;
  // Helper to clear a specific edition cover image
  const clearEditionImage = (editionId: string, imageType: 'frontCover' | 'spine' | 'backCover') => {
    setEditions(prev => prev.map(edition => {
      if (edition.id === editionId) {
        return {
          ...edition,
          images: {
            ...edition.images,
            [imageType]: null
          }
        };
      }
      return edition;
    }));
  };
  // Helper to remove an entire edition
  const removeEdition = (editionId: string) => {
    setEditions(prev => {
      const filtered = prev.filter(e => e.id !== editionId);
      // If we removed the selected edition, select the first remaining one
      if (selectedEditionId === editionId) {
        setSelectedEditionId(filtered.length > 0 ? filtered[0].id : null);
      }
      return filtered;
    });
  };
  return <div className={`p-6 rounded-xl shadow-sm ${dk.card}`} data-id="element-20864" data-mp="184s7iyi9xq45">
      <div className="flex items-center justify-between mb-4" data-id="element-20865" data-mp="1vvedmk8px0bi">
        <h2 className={`text-lg font-bold flex items-center ${dk.textHeading}`} data-id="element-20866" data-mp="20n5yce570btq">
          <ImageIcon className="w-5 h-5 mr-2 text-purple-500" data-id="element-20867" data-mp="20oz4ztft2w1q" />
          {productType === 'book' ? 'Editions & Media' : productType === 'event' ? 'Event Images' : productType === 'creative' || productType === 'service' ? 'Portfolio Images' : 'Product Images'}
        </h2>
        {productType === 'book' && <button onClick={() => setShowEditionModal(true)} className="text-sm bg-purple-600 text-white px-3 py-1.5 rounded-md hover:bg-purple-700 flex items-center" data-id="element-20868" data-mp="1wlg6tsp4nazb">
            <PlusIcon className="w-4 h-4 mr-1" data-id="element-20869" data-mp="1mrqw8lpqy6f6" /> Add Edition
          </button>}
      </div>

      {/* BOOK: Edition-based uploads */}
      {productType === 'book' ? <div className="space-y-6" data-id="element-20870" data-mp="1cx7d3e7xqwxp">
          {editions.length > 0 ? <>
              <div className={`flex space-x-1 border-b overflow-x-auto pb-1 ${dk.border}`} data-id="element-20871" data-mp="18t9dz3im1m4n">
                {editions.map(edition => <div key={edition.id} className="relative flex items-center group/tab" data-id="element-20872" data-mp="3fiuimai77bhl">
                    <button onClick={() => setSelectedEditionId(edition.id)} className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap
                      ${selectedEditionId === edition.id ? 'border-purple-600 text-purple-600 dark:text-purple-400' : `border-transparent ${dk.textMuted} hover:${dk.textSecondary}`}`} data-id="element-20873" data-mp="3kj8s7wzg6thq">
                      {edition.format.charAt(0).toUpperCase() + edition.format.slice(1)}
                    </button>
                    <button onClick={e => {
              e.stopPropagation();
              removeEdition(edition.id);
            }} className={`w-5 h-5 rounded-full flex items-center justify-center text-xs opacity-0 group-hover/tab:opacity-100 transition-opacity -ml-1 mr-1 ${darkMode ? 'bg-gray-700 text-gray-300 hover:bg-red-600 hover:text-white' : 'bg-gray-200 text-gray-500 hover:bg-red-500 hover:text-white'}`} title={`Remove ${edition.format} edition`} data-id="element-20874" data-mp="3ak00las21qyj">
                      <XIcon className="w-3 h-3" data-id="element-20875" data-mp="sl0chfmbcij8" />
                    </button>
                  </div>)}
              </div>

              {selectedEditionId && <div className="space-y-6 pt-4" data-id="element-20876" data-mp="12bbn1l0i5pz3">
                  <div className="grid grid-cols-2 gap-4" data-id="element-20877" data-mp="12atkk6n8yfiq">
                    <div data-id="element-20878" data-mp="1gbmhey10tmp0">
                      <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-20879" data-mp="1ga2x8wc8urhy">
                        ISBN
                      </label>
                      <input type="text" className={`w-full p-2 rounded-md border ${dk.inputOnCard}`} value={editions.find(e => e.id === selectedEditionId)?.metadata.isbn || ''} onChange={e => updateEditionMetadata(selectedEditionId, {
                isbn: e.target.value
              })} data-id="element-20880" data-mp="3i390mdeo8dax" />
                    </div>
                    <div data-id="element-20881" data-mp="2k7x1jbasx9bh">
                      <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-20882" data-mp="2k7n6vaetadzg">
                        Page Count
                      </label>
                      <input type="number" className={`w-full p-2 rounded-md border ${dk.inputOnCard}`} value={editions.find(e => e.id === selectedEditionId)?.metadata.pageCount || ''} onChange={e => updateEditionMetadata(selectedEditionId, {
                pageCount: parseInt(e.target.value)
              })} data-id="element-20883" data-mp="2bacx5thn6wwn" />
                    </div>
                    <div data-id="element-20884" data-mp="3u6y2lbfo29xb">
                      <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-20885" data-mp="3u7rboj23lbim">
                        Price
                      </label>
                      <div className="relative" data-id="element-20886" data-mp="qpzdgmxdbvbz">
                        <span className={`absolute left-3 top-1/2 -translate-y-1/2 ${dk.textMuted}`} data-id="element-20887" data-mp="qsc2fk1jn5yj">
                          $
                        </span>
                        <input type="number" className={`w-full pl-7 p-2 rounded-md border ${dk.inputOnCard}`} value={editions.find(e => e.id === selectedEditionId)?.metadata.price || ''} onChange={e => updateEditionMetadata(selectedEditionId, {
                  price: parseFloat(e.target.value)
                })} data-id="element-20888" data-mp="1eftocyy4n9tl" />
                      </div>
                    </div>
                    <div data-id="element-20889" data-mp="1zyu7uhu144rd">
                      <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-20890" data-mp="24y757wij0u87">
                        Stock
                      </label>
                      <input type="number" className={`w-full p-2 rounded-md border ${dk.inputOnCard}`} value={editions.find(e => e.id === selectedEditionId)?.metadata.stockQuantity || ''} onChange={e => updateEditionMetadata(selectedEditionId, {
                stockQuantity: parseInt(e.target.value)
              })} data-id="element-20891" data-mp="2eo0f8t30t1ac" />
                    </div>
                  </div>

                  {/* Cover Images */}
                  <div data-id="element-20892" data-mp="3dwx1x0qam5rb">
                    <label className={`block text-sm font-medium mb-2 ${dk.textSecondary}`} data-id="element-20893" data-mp="2q8m5qavjku9d">
                      Cover Images
                    </label>
                    <div className="grid grid-cols-[2fr_0.5fr_2fr] gap-3" data-id="element-20894" data-mp="2zzgsrhgm3xvv">
                      {([{
                key: 'frontCover',
                label: 'Front Cover',
                aspect: 'aspect-[2/3]'
              }, {
                key: 'spine',
                label: 'Spine',
                aspect: ''
              }, {
                key: 'backCover',
                label: 'Back Cover',
                aspect: 'aspect-[2/3]'
              }] as const).map(({
                key,
                label,
                aspect
              }) => {
                const file = editions.find(e => e.id === selectedEditionId)?.images[key];
                const refKey = `${selectedEditionId}-${key}`;
                return <div key={key} data-id="element-20895" data-mp="rtod5g0wqlkv">
                            <p className={`text-xs font-medium mb-1 text-center ${dk.textSecondary}`} data-id="element-20896" data-mp="wpn7rlctwztq">
                              {label}
                            </p>
                            <div className={`relative ${aspect || 'h-full'}`} data-id="element-20897" data-mp="3hi8h1cq68s64">
                              <label className={`block w-full h-full border-2 border-dashed rounded-lg cursor-pointer overflow-hidden transition-colors relative group ${dk.border} hover:border-purple-500`} data-id="element-20898" data-mp="15b7r15d7kzpb">
                                {file ? <>
                                    <img src={URL.createObjectURL(file)} alt={label} className="w-full h-full object-cover" onLoad={() => {}} data-id="element-20899" data-mp="llxdny0xqqud" />
                                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center" data-id="element-20900" data-mp="hjk9vf5r1308">
                                      <span className="text-white text-xs font-medium" data-id="element-20901" data-mp="2byd3x9qzhlq2">
                                        Change
                                      </span>
                                    </div>
                                  </> : <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-20902" data-mp="2uuln7tv2lboy">
                                    <UploadIcon className="w-5 h-5 text-gray-400 mb-1" data-id="element-20903" data-mp="2q455vyo4p5xb" />
                                    <span className="text-[10px] text-gray-500" data-id="element-20904" data-mp="34st3sblg5rqf">
                                      Upload
                                    </span>
                                  </div>}
                                <input ref={el => {
                        editionFileRefs.current[refKey] = el;
                      }} type="file" className="sr-only" accept="image/*" onChange={e => {
                        if (e.target.files?.[0]) {
                          handleEditionFileChange(selectedEditionId!, key, e.target.files[0]);
                        }
                      }} data-id="element-20905" data-mp="3ds6c30xu4a9k" />
                              </label>
                              {/* Remove button */}
                              {file && <button onClick={e => {
                      e.stopPropagation();
                      clearEditionImage(selectedEditionId!, key);
                      // Also reset the file input
                      const inputEl = editionFileRefs.current[refKey];
                      if (inputEl) inputEl.value = '';
                    }} className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center shadow-sm hover:bg-red-600 transition-colors z-10" title={`Remove ${label.toLowerCase()}`} data-id="element-20906" data-mp="b1cu4cec15qk">
                                  <XIcon className="w-3 h-3" data-id="element-20907" data-mp="2t6dzeuhvdai4" />
                                </button>}
                            </div>
                          </div>;
              })}
                    </div>
                    <p className={`text-[10px] ${dk.textMuted} mt-2 text-center`} data-id="element-20908" data-mp="2ejb1wntjr1us">
                      PNG, JPG, GIF up to 10MB
                    </p>
                  </div>
                </div>}
            </> : <div className={`text-center py-8 ${dk.textMuted}`} data-id="element-20909" data-mp="2rpp2f57en44y">
              <ImageIcon className="w-10 h-10 mx-auto mb-3 text-gray-400" data-id="element-20910" data-mp="2rrjoimuex2am" />
              <p className="text-sm mb-4" data-id="element-20911" data-mp="2vrtsxv2k3knd">No editions added yet.</p>
              <button onClick={() => setShowEditionModal(true)} className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 inline-flex items-center" data-id="element-20912" data-mp="2hrsqib6n855s">
                <PlusIcon className="w-4 h-4 mr-1" data-id="element-20913" data-mp="3agzptcdx8ys3" /> Add First Edition
              </button>
            </div>}
        </div> : productType === 'merch' ? <div className="space-y-4" data-id="element-20914" data-mp="30qesds9zi0ar">
          <div className="grid grid-cols-2 gap-3" data-id="element-20915" data-mp="30pc6jyriqo1p">
            {merchFiles.map((file, idx) => <div key={idx} className="relative group" data-id="element-20916" data-mp="35nx4tsn7mxni" data-mpi={idx}>
                <div className={`aspect-square rounded-lg overflow-hidden border ${dk.border}`} data-id="element-20917" data-mp="3tf2lub4vzvze">
                  <img src={URL.createObjectURL(file)} alt={`Product ${idx + 1}`} className="w-full h-full object-cover" data-id="element-20918" data-mp="216j1p9e71o8" />
                </div>
                <button onClick={() => setMerchFiles(prev => prev.filter((_, i) => i !== idx))} className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-20919" data-mp="1hr761k36ivf7">
                  <XIcon className="w-3 h-3" data-id="element-20920" data-mp="yt4lbhhnzkn7" />
                </button>
              </div>)}
            <label className={`aspect-square border-2 border-dashed rounded-lg cursor-pointer flex flex-col items-center justify-center transition-colors ${dk.border} hover:border-amber-500`} data-id="element-20921" data-mp="1csmgceljzu50">
              <UploadIcon className="w-8 h-8 text-gray-400 mb-2" data-id="element-20922" data-mp="2o682rs013hmu" />
              <span className="text-xs text-gray-500" data-id="element-20923" data-mp="2j8y5jq6ee1og">Add Image</span>
              <input type="file" className="sr-only" accept="image/*" onChange={e => {
            if (e.target.files?.[0]) setMerchFiles(prev => [...prev, e.target.files![0]]);
          }} data-id="element-20924" data-mp="2jasrn7tenzu4" />
            </label>
          </div>
          <p className={`text-xs ${dk.textMuted}`} data-id="element-20925" data-mp="1uv1p43e2oave">
            Upload product photos. Square or landscape recommended.
          </p>
        </div> : productType === 'event' ? <div className="space-y-4" data-id="element-20926" data-mp="125mcj96re9xd">
          <div data-id="element-20927" data-mp="126xcsz0zwzjq">
            <p className={`text-xs font-medium mb-1 ${dk.textSecondary}`} data-id="element-20928" data-mp="126dw1ln30h7a">
              Event Image
            </p>
            <div className="relative" data-id="element-20929" data-mp="173e1pvg0rhxv">
              <label className={`block aspect-video border-2 border-dashed rounded-lg cursor-pointer overflow-hidden transition-colors relative group ${dk.border} hover:border-teal-500`} data-id="element-20930" data-mp="1bw76ukfegsah">
                {eventImageFile ? <>
                    <img src={URL.createObjectURL(eventImageFile)} alt="Event" className="w-full h-full object-cover" data-id="element-20931" data-mp="bvvcdgwzdppl" />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center" data-id="element-20932" data-mp="1xehi8ux8qy9">
                      <span className="text-white text-xs font-medium" data-id="element-20933" data-mp="3u1gngpzevub4">
                        Change
                      </span>
                    </div>
                  </> : <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-20934" data-mp="3kam0fjeccqom">
                    <UploadIcon className="w-8 h-8 text-gray-400 mb-2" data-id="element-20935" data-mp="3fd3rht0bqbgm" />
                    <span className="text-xs text-gray-500" data-id="element-20936" data-mp="3af29zcijqk79">
                      Upload event image
                    </span>
                    <span className="text-[10px] text-gray-400 mt-1" data-id="element-20937" data-mp="36e8m2j53t5ed">
                      Landscape (16:9)
                    </span>
                  </div>}
                <input type="file" className="sr-only" accept="image/*" onChange={e => {
              if (e.target.files?.[0]) setEventImageFile(e.target.files[0]);
            }} data-id="element-20938" data-mp="t5o5rpjvm2um" />
              </label>
              {eventImageFile && <button onClick={() => setEventImageFile(null)} className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center shadow-sm hover:bg-red-600 transition-colors z-10" title="Remove event image" data-id="element-20939" data-mp="add94dftf14y">
                  <XIcon className="w-3 h-3" data-id="element-20940" data-mp="5fk59r1zij7u" />
                </button>}
            </div>
          </div>
          <div data-id="element-20941" data-mp="3nl6umz52pvpg">
            <p className={`text-xs font-medium mb-1 ${dk.textSecondary}`} data-id="element-20942" data-mp="3nmrdvslxts80">
              Banner Image
            </p>
            <div className="relative" data-id="element-20943" data-mp="38xg9y003dfnm">
              <label className={`block aspect-[3/1] border-2 border-dashed rounded-lg cursor-pointer overflow-hidden transition-colors relative group ${dk.border} hover:border-teal-500`} data-id="element-20944" data-mp="38yhm67sug86x">
                {eventBannerFile ? <>
                    <img src={URL.createObjectURL(eventBannerFile)} alt="Banner" className="w-full h-full object-cover" data-id="element-20945" data-mp="3dwjezkg2nlvl" />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center" data-id="element-20946" data-mp="3mwfzrazcc0ld">
                      <span className="text-white text-xs font-medium" data-id="element-20947" data-mp="5isqfv6gedlw">
                        Change
                      </span>
                    </div>
                  </> : <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-20948" data-mp="efbhf1v4jrfu">
                    <UploadIcon className="w-6 h-6 text-gray-400 mb-1" data-id="element-20949" data-mp="9gqe2tkksrn6" />
                    <span className="text-xs text-gray-500" data-id="element-20950" data-mp="lzq8wzhbqtha">Upload banner</span>
                    <span className="text-[10px] text-gray-400 mt-1" data-id="element-20951" data-mp="hyxw5w98u6g9">
                      Wide (3:1)
                    </span>
                  </div>}
                <input type="file" className="sr-only" accept="image/*" onChange={e => {
              if (e.target.files?.[0]) setEventBannerFile(e.target.files[0]);
            }} data-id="element-20952" data-mp="qzw2wmb2gs2j" />
              </label>
              {eventBannerFile && <button onClick={() => setEventBannerFile(null)} className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center shadow-sm hover:bg-red-600 transition-colors z-10" title="Remove banner image" data-id="element-20953" data-mp="d2pjcpiiqpc9">
                  <XIcon className="w-3 h-3" data-id="element-20954" data-mp="1r82ivlef8z4i" />
                </button>}
            </div>
          </div>
        </div> : productType === 'bookbox' ? <div className="space-y-4" data-id="element-20955" data-mp="1w15fy3ieqqpg">
          <div className="grid grid-cols-2 gap-3" data-id="element-20956" data-mp="1i8p7gn6qm0h5">
            {bookBoxFiles.map((file, idx) => <div key={idx} className="relative group" data-id="element-20957" data-mp="1m8pxz674224k" data-mpi={idx}>
                <div className={`aspect-square rounded-lg overflow-hidden border ${dk.border}`} data-id="element-20958" data-mp="1m75139xbq73j">
                  <img src={URL.createObjectURL(file)} alt={`Box ${idx + 1}`} className="w-full h-full object-cover" data-id="element-20959" data-mp="2eyopd88s2bce" />
                </div>
                <button onClick={() => setBookBoxFiles(prev => prev.filter((_, i) => i !== idx))} className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-20960" data-mp="25wfj4ukjl44t">
                  <XIcon className="w-3 h-3" data-id="element-20961" data-mp="2r8ec6ll3tvnt" />
                </button>
              </div>)}
            <label className={`aspect-square border-2 border-dashed rounded-lg cursor-pointer flex flex-col items-center justify-center transition-colors ${dk.border} hover:border-pink-500`} data-id="element-20962" data-mp="2mhfv3jsaktji">
              <UploadIcon className="w-8 h-8 text-gray-400 mb-2" data-id="element-20963" data-mp="2dh9lwa3mmfq5" />
              <span className="text-xs text-gray-500" data-id="element-20964" data-mp="2den5rm2o6rwb">Add Image</span>
              <input type="file" className="sr-only" accept="image/*" onChange={e => {
            if (e.target.files?.[0]) setBookBoxFiles(prev => [...prev, e.target.files![0]]);
          }} data-id="element-20965" data-mp="28gljytdv0eh5" />
            </label>
          </div>
          <p className={`text-xs ${dk.textMuted}`} data-id="element-20966" data-mp="3tviy65ulge0m">
            Upload box photos, unboxing shots, or contents previews.
          </p>
        </div> : productType === 'creative' || productType === 'service' ? <div className="space-y-4" data-id="element-20967" data-mp="1klryi4aynur6">
          {(productType === 'creative' ? creativePortfolioFiles : servicePortfolioFiles).map((item, idx) => <div key={idx} className={`rounded-lg border overflow-hidden ${dk.border}`} data-id="element-20968" data-mp="1ten5p3jzo0jl" data-mpi={idx}>
              <div className={`${item.aspectRatio === 'portrait' ? 'aspect-[2/3]' : item.aspectRatio === 'landscape' ? 'aspect-video' : 'aspect-square'} relative group`} data-id="element-20969" data-mp="wpntxek74gmn">
                <img src={URL.createObjectURL(item.file)} alt={`Portfolio ${idx + 1}`} className="w-full h-full object-cover" data-id="element-20970" data-mp="15oj38zije6u6" />
                <button onClick={() => {
            if (productType === 'creative') setCreativePortfolioFiles(prev => prev.filter((_, i) => i !== idx));else setServicePortfolioFiles(prev => prev.filter((_, i) => i !== idx));
          }} className="absolute top-2 right-2 w-7 h-7 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-20971" data-mp="2h3o6c4mky7eb">
                  <XIcon className="w-3.5 h-3.5" data-id="element-20972" data-mp="33iwm942jry6e" />
                </button>
              </div>
              <div className={`px-3 py-2 flex items-center justify-between ${dk.surfaceMuted}`} data-id="element-20973" data-mp="2yk24m5gbgfnv">
                <span className="text-xs text-gray-500 capitalize" data-id="element-20974" data-mp="38c7xdhee7144">
                  {item.aspectRatio}
                </span>
              </div>
            </div>)}
          <div data-id="element-20975" data-mp="3m9ebgyo68bn0">
            <p className={`text-xs font-medium mb-2 ${dk.textSecondary}`} data-id="element-20976" data-mp="3hcxow8wme5c1">
              Add portfolio image
            </p>
            <div className="grid grid-cols-3 gap-2" data-id="element-20977" data-mp="4ss9pa38vfiv">
              {[{
            ratio: 'portrait' as const,
            aspect: 'aspect-[2/3]',
            label: 'Portrait',
            desc: '2:3'
          }, {
            ratio: 'landscape' as const,
            aspect: 'aspect-video',
            label: 'Landscape',
            desc: '16:9'
          }, {
            ratio: 'square' as const,
            aspect: 'aspect-square',
            label: 'Square',
            desc: '1:1'
          }].map(({
            ratio,
            aspect,
            label,
            desc
          }) => <label key={ratio} className={`border-2 border-dashed rounded-lg cursor-pointer flex flex-col items-center justify-center p-3 transition-colors ${dk.border} hover:border-violet-500`} data-id="element-20978" data-mp="3d9ws1j7gnooe">
                  <div className={`${aspect} w-full max-w-[60px] border rounded mb-2 flex items-center justify-center ${dk.surfaceMuted} ${dk.border}`} data-id="element-20979" data-mp="19ryjv6oc3qjn">
                    <UploadIcon className="w-4 h-4 text-gray-400" data-id="element-20980" data-mp="14zf6oatk6xfd" />
                  </div>
                  <span className={`text-[11px] font-medium ${dk.textPrimary}`} data-id="element-20981" data-mp="1002inmqybkb7">
                    {label}
                  </span>
                  <span className={`text-[9px] ${dk.textMuted}`} data-id="element-20982" data-mp="1srcjo58qvvi2">{desc}</span>
                  <input type="file" className="sr-only" accept="image/*" onChange={e => {
              if (e.target.files?.[0]) {
                const newItem = {
                  file: e.target.files[0],
                  aspectRatio: ratio
                };
                if (productType === 'creative') setCreativePortfolioFiles(prev => [...prev, newItem]);else setServicePortfolioFiles(prev => [...prev, newItem]);
              }
            }} data-id="element-20983" data-mp="1nrq59sh6k97o" />
                </label>)}
            </div>
          </div>
        </div> : <label className={`block border-2 border-dashed rounded-xl p-8 cursor-pointer transition-colors ${dk.border} hover:border-purple-500 ${darkMode ? 'hover:bg-gray-750' : 'hover:bg-purple-50'}`} data-id="element-20984" data-mp="2ssr51gohl0iv">
          <div className="flex flex-col items-center justify-center text-center" data-id="element-20985" data-mp="37nx8bvw76l8i">
            <UploadIcon className="w-10 h-10 text-gray-400 mb-3" data-id="element-20986" data-mp="37m2pqo8ef57l" />
            <p className={`text-sm font-medium ${dk.textPrimary}`} data-id="element-20987" data-mp="3boz0jsrp5x0z">
              Click to upload or drag and drop
            </p>
            <p className={`text-xs ${dk.textMuted} mt-1`} data-id="element-20988" data-mp="1dc8s7qe8ccmj">
              SVG, PNG, JPG or GIF (max. 3MB)
            </p>
          </div>
          <input type="file" className="sr-only" accept="image/*" multiple data-id="element-20989" data-mp="yepkqufj9w56" />
        </label>}
    </div>;
};